import React, { useEffect, useState } from "react";
import {
  Tabs,
  TabList,
  Tab,
  Box,
  Flex,
  useTabsContext,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PAGINATION_DEFAULTS } from "@root/constants";
import { useDashboard } from "@root/hooks/useDashboard";
import { useSearchParams } from "react-router-dom";
import SectionHeader from "@root/components/section-header/sectionheader";
import DashboardSection from "./reports-section";
import { ChatSection } from "./chat-section";

const TabIconLabel = ({ idx, label }: { idx: number; label: string }) => {
  const { selectedIndex } = useTabsContext();
  const isSelected = selectedIndex === idx;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      fontWeight={isSelected ? "bold" : "normal"}
    >
      {label}
    </Box>
  );
};

const CategoryTabs: React.FC = () => {
  const { t } = useTranslation("common");
  const [searchParams] = useSearchParams();
  const [defaultTabIndex, setDefaultTabIndex] = useState(0);

  const { fetchReports, PageNo } = useDashboard();

  const payload = {
    page: PageNo,
    limit: PAGINATION_DEFAULTS.PAGESIZE,
  };

  useEffect(() => {
    fetchReports(payload);
  }, []);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    setDefaultTabIndex(tabParam === "chat-history" ? 1 : 0);
  }, [searchParams]);

  const tabData = [
    { label: t("dashboard.reports") },
    { label: t("dashboard.history") },
  ];

  const handleTabChange = (index: number) => {
    setDefaultTabIndex(index);
  };

  return (
    <Flex
      flexDirection="column"
      height="100%"
      overflow="hidden"
      background="white"
      borderRadius="10px"
      boxShadow="0px 1px 4px 0px #0000001F"
      border="0.5px solid #A7B2BA"
    >
      {/* Header Section - Fixed at top */}
      <Flex
        padding="24px"
        justifyContent="space-between"
        alignItems="center"
        flexShrink={0}
      >
        <SectionHeader
          title={
            defaultTabIndex === 0
              ? t("dashboard.recentReports")
              : t("dashboard.recentHistory")
          }
          onViewAll={false}
        />
        <Tabs
          variant="unstyled"
          isFitted={false}
          index={defaultTabIndex}
          onChange={handleTabChange}
        >
          <TabList
            background="#CDD6DC3D"
            borderRadius="100px"
            display="flex"
            whiteSpace="nowrap"
            boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.1), 0px 0px 4px 0px rgba(0, 0, 0, 0.05)"
            border="1px solid #E6E6E6"
          >
            {tabData?.map((tab, idx) => (
              <Tab
                key={tab?.label}
                fontWeight="600"
                fontSize="16px"
                color="#798692"
                _selected={{
                  color: "#0FABBE",
                  bg: "white",
                  borderRadius: "100px",
                }}
                _focus={{ boxShadow: "none" }}
              >
                <TabIconLabel idx={idx} label={tab.label} />
              </Tab>
            ))}
          </TabList>
        </Tabs>
      </Flex>

      {/* Content Section - Scrollable */}
      <Box
        flex={1}
        minHeight={0}
        paddingX="24px"
        paddingBottom="24px"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        {defaultTabIndex === 0 ? <DashboardSection /> : <ChatSection />}
      </Box>
    </Flex>
  );
};

export default CategoryTabs;
